import React from 'react';
import type { ExperienceItem, Bullet } from '../../data/cv';
import { Bullets } from './Bullets';
import { CompanyMark } from './CompanyMark';
import { TechStack, InlineStack } from './TechStack';
import { periodEndpoints, splitLocation, roleDuration } from './helpers';
interface Props {
  item: ExperienceItem;
  isLast: boolean;
  compact?: boolean;
}

/**
 * Editorial experience entry: a left "spine" carrying the year range + duration,
 * and a right content well with a timeline rule/node in the gutter.
 */
export function ExperienceEntry({
  item,
  isLast,
  compact = false
}: Props) {
  const {
    start,
    end,
    same
  } = periodEndpoints(item.period);
  const {
    place,
    mode
  } = splitLocation(item.location);
  const duration = roleDuration(item.period);
  const compactBullets: Bullet[] = compact ? [...(item.bullets ?? []), ...(item.subsections?.flatMap(s => s.bullets) ?? [])].slice(0, 2) : [];
  return <article className="cv-entry group relative grid grid-cols-[82px_1fr] gap-6" data-id="element-105" data-mp="g1fb3dd6tmn5">
      {/* spine: month/year range (stacked) + duration */}
      <div className="flex flex-col items-end pt-[2px] text-right" data-id="element-106" data-mp="1bx4p4m9uavhw">
        <span className="whitespace-nowrap font-mono text-[11px] font-medium tabular-nums leading-none text-[var(--ink)]" data-id="element-107" data-mp="1bva477ykj3dt">
          {end}
        </span>
        {!same && <span className="mt-1 whitespace-nowrap font-mono text-[10px] tabular-nums leading-none text-[var(--ink-faint)]" data-id="element-108" data-mp="1kqhoa5yhytcf">
            {start}
          </span>}
        {duration && <span className="mt-2 rounded-full bg-[var(--accent)]/10 px-1.5 py-[1px] font-mono text-[8px] uppercase tracking-[0.1em] text-[var(--accent)]" data-id="element-109" data-mp="1umkqfbtcmrzd">
            {duration}
          </span>}
      </div>

      {/* vertical rule in the gutter + accent node (timeline) */}
      <div aria-hidden="true" className="pointer-events-none absolute bottom-0 top-2 w-px bg-[var(--line)]" style={{
      left: 'calc(82px + 0.75rem)'
    }} data-id="element-110" data-mp="59pl2qza9w9v">
        <span className="absolute left-1/2 top-[16px] h-[9px] w-[9px] -translate-x-1/2 rounded-full border-2 border-[var(--accent)] bg-[var(--paper)]" data-id="element-111" data-mp="1ckx52ak9x2o6" />
      </div>

      {/* content */}
      <div className={isLast ? 'pb-0' : compact ? 'pb-5' : 'pb-8'} data-id="element-112" data-mp="1lm4zuinkzldi">
        <div className="keep-with-next flex items-start gap-3.5" data-id="element-113" data-mp="1lng40jkxuv55">
          <CompanyMark company={item.company} mark={item.mark} domain={item.domain} logo={item.logo} data-id="element-114" data-mp="1hjgr0ayvaegj" />
          <div className="min-w-0 flex-1" data-id="element-115" data-mp="1ql80zs0fx7g7">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5" data-id="element-116" data-mp="1qkf0v9vzlg71">
              <h3 className="font-display text-[19px] font-medium leading-tight text-[var(--ink)]" data-id="element-117" data-mp="258szf8mdeihs">
                {item.company}
              </h3>
              <div className="flex items-center gap-2.5" data-id="element-118" data-mp="2eash8357lqls">
                {item.industry && <span className="rounded-full bg-[var(--accent)]/10 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--accent)]" data-id="element-119" data-mp="2e8g5w8tyiehp">
                    {item.industry}
                  </span>}
                {mode && <span className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--ink-faint)]" data-id="element-120" data-mp="2xw2gy4epqngm">
                    {mode}
                  </span>}
              </div>
            </div>
            <p className="mt-1 text-[12px] font-medium uppercase tracking-[0.1em] text-[var(--ink-soft)]" data-id="element-121" data-mp="2o0hksmwbshes">
              {item.title}
              {place && <span className="ml-2 font-normal normal-case tracking-normal text-[var(--ink-faint)]" data-id="element-122" data-mp="2eyrsleumdefy">
                  · {place}
                </span>}
            </p>
          </div>
        </div>

        <div className="pl-[58px]" data-id="element-123" data-mp="258ge4ydb7mut">
          {compact ? <>
              {item.summary?.[0] && <p className="mt-2 text-[11.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-124" data-mp="1ra8m94v4rbqi">
                  {item.summary[0]}
                </p>}
              {compactBullets.length > 0 && <div className="mt-2 gap-x-10 sm:columns-2" data-id="element-125" data-mp="1o8dk3v0yg4do">
                  <Bullets items={compactBullets} data-id="element-126" data-mp="1j8r79mp1gq5e" />
                </div>}
              {item.stack && item.stack.length > 0 && <div className="mt-2.5" data-id="element-127" data-mp="1af4e84c7u0zw">
                  <InlineStack stack={item.stack} data-id="element-128" data-mp="1ae33k0z43gip" />
                </div>}
            </> : <>
              {item.summary && <div className="mt-3 max-w-[64ch] space-y-2" data-id="element-129" data-mp="10h83hdn6x82b">
                  {item.summary.map((p, i) => <p key={i} className="text-[12.5px] leading-relaxed text-[var(--ink-soft)]" data-id="element-130" data-mp="vhc3dphm60s1" data-mpi={i}>
                      {p}
                    </p>)}
                </div>}
              {item.bullets && <div className="mt-3.5 max-w-[68ch] gap-x-10 sm:columns-2" data-id="element-131" data-mp="2lu1gifxeump7">
                  <Bullets items={item.bullets} data-id="element-132" data-mp="7ms5khcafc4l" />
                </div>}
              {item.subsections && <div className="mt-4 gap-x-10 sm:columns-2" data-id="element-133" data-mp="cktn2xu2f3dy">
                  {item.subsections.map((sub, i) => <div key={i} className="mb-4 break-inside-avoid last:mb-0" data-id="element-134" data-mp="hdmqnidss5og" data-mpi={i}>
                      <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink)]" data-id="element-135" data-mp="le6q4slufldr">
                        {sub.heading}
                      </p>
                      <Bullets items={sub.bullets} data-id="element-136" data-mp="3prve6c1gq69r" />
                    </div>)}
                </div>}
              {item.stack && item.stack.length > 0 && <TechStack stack={item.stack} data-id="element-137" data-mp="2nfko24z8f4n" />}
            </>}
        </div>
      </div>
    </article>;
}